/**
 * TopResidentsTable — Top 10 residents by alarm volume.
 */

import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { exportWellnessCsv } from '../../../../services/csvExport.js';
import { formatDateNoYear, getToday } from '../../../../libs.jsx';

// V1 thresholds: careLow=0.5, careMed=1.0, careHigh=2.0
// Uses numeric review_index field, not review_index_name (which has area-type prefixes like "AL Medium")
const CARE_LOW = 0.5;
const CARE_MED = 1.0;
const CARE_HIGH = 2.0;

function getReviewLevel(reviewIndex, is60days) {
    if (!is60days) return null;
    const val = parseFloat(reviewIndex);
    if (isNaN(val) || val <= CARE_LOW) return null;
    if (val > CARE_HIGH) return 'extreme';
    if (val > CARE_MED) return 'medium';
    return 'low';
}

const ROW_CLASS = { low: 'md-row--low', medium: 'md-row--medium', high: 'md-row--high', extreme: 'md-row--extreme' };
const BADGE_CLASS = { low: 'md-review-badge--low', medium: 'md-review-badge--medium', high: 'md-review-badge--high', extreme: 'md-review-badge--extreme' };

const LEVEL_INFO = {
    low:     { label: 'Low',     description: 'Review index above 0.5' },
    medium:  { label: 'Medium',  description: 'Review index above 1.0' },
    extreme: { label: 'High',    description: 'Review index above 2.0' },
};

const REVIEW_TOOLTIP = [
    'The Review Index is the average number of alerts a resident triggered per day over the last 60 days —',
    'higher values mean more frequent calls for aid.',
    'To make sure the score reflects an established pattern, color highlighting is only applied to Assisted Living residents who have lived here at least 60 days and have had recent alert activity.',
];

const TOP_RESIDENTS_TOOLTIP = [
    'This table lists the top 10 residents that call for aid the most over the last 28 days.',
    'The Total column is the average daily events across that 28-day window,',
    'and each weekly column shows the average daily events for that specific week,',
    'so you can spot trends in resident need over time.',
];

/* ── HeaderInfoTip — generic ⓘ icon with hover popover ── */
const HeaderInfoTip = ({ title, body }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const [style, setStyle] = useState({});

    const handleEnter = () => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const spaceRight = window.innerWidth - rect.left;
        const spaceBelow = window.innerHeight - rect.bottom;

        setStyle({
            position: 'fixed',
            left: spaceRight < 320 ? undefined : `${rect.left}px`,
            right: spaceRight < 320 ? '16px' : undefined,
            top: spaceBelow < 100 ? undefined : `${rect.bottom + 6}px`,
            bottom: spaceBelow < 100 ? `${window.innerHeight - rect.top + 6}px` : undefined,
            maxWidth: '300px',
            whiteSpace: 'normal',
        });
        setOpen(true);
    };

    return (
        <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)} style={{ cursor: 'help', marginLeft: '6px', color: 'var(--color-text-muted)' }}>
            <i className="fe fe-info" style={{ fontSize: 'var(--font-size-sm)' }} />
            {open && (
                <span className="na-type-popover" style={style}>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)' }}>{title}</span>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{body}</span>
                </span>
            )}
        </span>
    );
};

/* ── ReviewBadge — badge pill with hover popover showing level & value ── */
const ReviewBadge = ({ name, level, reviewIndex }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const [style, setStyle] = useState({});

    const handleEnter = () => {
        if (!level || !ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const spaceRight = window.innerWidth - rect.left;
        const spaceBelow = window.innerHeight - rect.bottom;

        setStyle({
            position: 'fixed',
            left: spaceRight < 216 ? undefined : `${rect.left}px`,
            right: spaceRight < 216 ? '16px' : undefined,
            top: spaceBelow < 60 ? undefined : `${rect.bottom + 4}px`,
            bottom: spaceBelow < 60 ? `${window.innerHeight - rect.top + 4}px` : undefined,
        });
        setOpen(true);
    };

    if (!level) return <span>{name || '—'}</span>;

    const info = LEVEL_INFO[level];

    return (
        <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)} style={{ cursor: 'help' }}>
            <span className={`md-review-badge ${BADGE_CLASS[level]}`}>{name || '—'}</span>
            {open && (
                <span className="na-type-popover" style={style}>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Review Level: {info.label}</span>
                    <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-heading)' }}>Index: {reviewIndex} — {info.description}</span>
                </span>
            )}
        </span>
    );
};

const weekRanges = [
    `${formatDateNoYear(getToday(-7))}–${formatDateNoYear(getToday(-1))}`,
    `${formatDateNoYear(getToday(-14))}–${formatDateNoYear(getToday(-8))}`,
    `${formatDateNoYear(getToday(-21))}–${formatDateNoYear(getToday(-15))}`,
    `${formatDateNoYear(getToday(-28))}–${formatDateNoYear(getToday(-22))}`,
];

const CSV_COLS = [
    { key: 'review_index_name', label: 'Review Index' },
    { key: 'resident_name', label: 'Resident' },
    { key: 'area_name', label: 'Area' },
    { key: 'avg_daily_events', label: 'Total' },
    { key: 'week1', label: `Week 1` },
    { key: 'week2', label: `Week 2` },
    { key: 'week3', label: `Week 3` },
    { key: 'week4', label: `Week 4` },
];

const TopResidentsTable = ({ data }) => {
    const rows = Array.isArray(data) ? data.slice(0, 10) : [];

    // ehr_level_of_care is sparsely populated — only surface it when at least one
    // of the top-10 residents actually has a value, so we don't add visual noise
    // (or an empty CSV column) when the EHR isn't providing it.
    const hasEhrLoc = rows.some((r) => (r.ehr_level_of_care ?? '').toString().trim() !== '');
    const csvCols = hasEhrLoc
        ? CSV_COLS.flatMap((c) => (c.key === 'resident_name'
            ? [c, { key: 'ehr_level_of_care', label: 'Level of Care' }]
            : [c]))
        : CSV_COLS;

    return (
        <div className="md-section">
            <div className="md-section__header">
                <h3 className="md-section__title">Top 10 Resident Review<HeaderInfoTip title="About This Table" body={TOP_RESIDENTS_TOOLTIP.join(' ')} /></h3>
                {rows.length > 0 ? (
                    <button className="rp-btn rp-btn--secondary" onClick={() => exportWellnessCsv(rows, csvCols, 'top-residents.csv')} style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px' }}>
                        <i className="fe fe-download" style={{ marginRight: '4px' }} />CSV
                    </button>
                ) : null}
            </div>
            <div className="dp-table-wrap">
                <table className="dp-table">
                    <thead className="dp-thead">
                        <tr>
                            <th rowSpan={2} className="dp-th">Review<HeaderInfoTip title="About Review Index" body={REVIEW_TOOLTIP.join(' ')} /></th>
                            <th rowSpan={2} className="dp-th">Resident{hasEhrLoc ? <HeaderInfoTip title="Level of Care" body="The muted sub-label under a resident's name is their EHR level of care, shown only when the EHR provides it." /> : null}</th>
                            <th rowSpan={2} className="dp-th">Area</th>
                            <th rowSpan={2} className="dp-th" style={{ textAlign: 'center' }}>Total Events</th>
                            <th colSpan={5} className="dp-th" style={{ textAlign: 'center', borderBottomColor: 'lightgrey' }}>Average Daily Events</th>
                        </tr>
                        <tr>
                            <th className="dp-th" style={{ textAlign: 'center' }}>{weekRanges[3]}</th>
                            <th className="dp-th" style={{ textAlign: 'center' }}>{weekRanges[2]}</th>
                            <th className="dp-th" style={{ textAlign: 'center' }}>{weekRanges[1]}</th>
                            <th className="dp-th" style={{ textAlign: 'center' }}>{weekRanges[0]}</th>
                            <th className="dp-th" style={{ textAlign: 'center' }}>Full 28 Days</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr className="dp-tr"><td className="dp-td" colSpan={8} style={{ textAlign: 'center' }}><span className="dp-cell-text dp-cell-text--muted">No resident data</span></td></tr>
                        ) : null}
                        {rows.map((r, i) => {
                            const level = getReviewLevel(r.review_index, r.is60days);
                            const ehrLoc = (r.ehr_level_of_care ?? '').toString().trim();
                            return (
                            <tr key={i} className={`dp-tr ${level ? ROW_CLASS[level] : ''}`}>
                                <td className="dp-td"><ReviewBadge name={r.review_index_name} level={level} reviewIndex={r.review_index} /></td>
                                <td className="dp-td">
                                    {r.resident_id ? <Link to={`/profiles/resident/${r.resident_id}`} className="pp-field__value--link">{r.resident_name}</Link> : (r.resident_name || '—')}
                                    {hasEhrLoc && ehrLoc ? (
                                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: '2px', lineHeight: 1.2 }} title="EHR level of care">{ehrLoc}</div>
                                    ) : null}
                                </td>
                                <td className="dp-td">{r.area_id ? <Link to={`/profiles/area/${r.area_id}`} className="pp-field__value--link">{r.area_name}</Link> : (r.area_name || '—')}</td>
                                <td className="dp-td" style={{ textAlign: 'center' }}>{r.total_events ?? '-'}</td>
                                <td className="dp-td" style={{ textAlign: 'center' }}>{r.week4 ?? '-'}</td>
                                <td className="dp-td" style={{ textAlign: 'center' }}>{r.week3 ?? '-'}</td>
                                <td className="dp-td" style={{ textAlign: 'center' }}>{r.week2 ?? '-'}</td>
                                <td className="dp-td" style={{ textAlign: 'center' }}>{r.week1 ?? '-'}</td>
                                <td className="dp-td" style={{ textAlign: 'center' }}>{r.avg_daily_events ?? '-'}</td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TopResidentsTable;
